import { isoUUID, UUID } from '@macs-frontend/core/utils/uuid';
import { sortByCreatedAt } from '@macs-frontend/features/shared/utils/date';
import {
  ActionTeam,
  BudgetItemForServer,
  DictionaryMaterial,
  emptyReccuringMaterial,
  emptySampleMaterial,
  MaterialGroupItem,
  MaterialGroupTypes,
  ProjectMaterialGroup,
  ProjectMaterialGroupItem,
} from '@macs-frontend/web-macs/shared';
import { ActionTeamType } from '@macs-gql';
import { FinancialUtils } from '../../../shared';
import { BudgetItemForm } from '../models/budget-item-form';
import {
  MaterialsGroupItemsTotal,
  MTMaterialGroupForm,
  MTMaterialGroupItem,
} from '../reducers/materials-management';

export interface MaterialsTotalCalc {
  groups: MTMaterialGroupForm[];
  materialsTotal: MaterialsGroupItemsTotal;
}

export interface FormatedProjectMaterialsGroupItems {
  [projectMaterialId: string]: {
    name: string;
    qtyRequired: number;
    dictionaryMaterial: string;
    qtyLimit: number;
    totalEstimated: number;
    totalTransported: number;
    retourPrice: number;
    billablePrice: number;
    unitPrice: number;
    totalBillablePrice: number;
    totalUnitPrice: number;
    reserveUnit: number;
    reservePercent: number;
    qtyTotal: number;
    materialGroupItems: FormattedMaterialGroupItem;
    budgetItem?: BudgetItemForServer;
    isHiddenForBudgetOverview?: boolean;
  };
}

export interface FormattedMaterialGroupItem {
  [materialGroupId: string]: {
    actionTeam: string;
    estimated: number;
    qty: number;
    transported: number;
  };
}

export interface FormattedMaterialGroups {
  [groupId: string]: {
    uuid: UUID;
    type: MaterialGroupTypes;
    name: string;
    project: string;
    projectMaterialGroupItems: FormatedProjectMaterialsGroupItems;
  };
}

// @ts-expect-error
export const emptyMTMaterialGroupItem = {
  qty: null,
  estimated: null,
  transported: null,
  actionTeam: null,
  uuid: null,
} as MTMaterialGroupItem;

export class MTCalculations {
  static calcMaterialsTotal(
    materialGroupForm: MTMaterialGroupForm[],
  ): MaterialsTotalCalc {
    const materialsTotal: MaterialsGroupItemsTotal = {};
    const groups: MTMaterialGroupForm[] = materialGroupForm.map(group => ({
      ...group,
      projectMaterialGroupItems: group.projectMaterialGroupItems.map(item => {
        const materialTotal: {
          qty: number;
          transported: number;
          estimated: number;
        } = {
          qty: 0,
          transported: 0,
          estimated: 0,
        };
        item.materialGroupItemsForServer.map(material => {
          materialTotal.qty += +material.qty;
          materialTotal.transported += +material.transported;
          materialTotal.estimated += +material.estimated;
        });
        materialsTotal[isoUUID.unwrap(item.uuid)] = materialTotal;
        return {
          ...item,
          totalTransported: materialTotal.transported,
          totalEstimated: materialTotal.estimated,
          qtyRequired: materialTotal.qty,
        };
      }),
    }));
    return {
      materialsTotal,
      groups,
    };
  }

  static prepareMaterialsForServer(
    materialsGroup: MTMaterialGroupForm[],
    budgetItemsForm?: BudgetItemForm[],
  ): FormattedMaterialGroups {
    const formattedMaterialGroup: FormattedMaterialGroups = {};
    // eslint-disable-next-line functional/no-let
    let index = 0;
    materialsGroup.map(group => {
      formattedMaterialGroup[isoUUID.unwrap(group.uuid)] = {
        uuid: group.uuid,
        type: group.type,
        name: group.name,
        // @ts-expect-error
        project: null,
        projectMaterialGroupItems: {},
      };
      group.projectMaterialGroupItems.map(materialItem => {
        formattedMaterialGroup[isoUUID.unwrap(group.uuid)].projectMaterialGroupItems[
          isoUUID.unwrap(materialItem.uuid)
        ] = {
          name: materialItem.name,
          qtyRequired: materialItem.qtyRequired,
          // @ts-expect-error
          totalTransported: materialItem.totalTransported,
          // @ts-expect-error
          totalEstimated: materialItem.totalEstimated,
          // @ts-expect-error
          dictionaryMaterial: materialItem.dictionaryMaterial
            ? materialItem.dictionaryMaterial.uuid
            : null,
          qtyLimit: materialItem.qtyLimit,

          // @ts-expect-error
          qtyTotal: materialItem.qtyTotal,
          // @ts-expect-error
          reserveUnit: materialItem.reserveUnit,
          // @ts-expect-error
          reservePercent: materialItem.reservePercent,
          totalBillablePrice: materialItem.totalBillablePrice,
          totalUnitPrice: materialItem.totalUnitPrice,

          retourPrice: FinancialUtils.toCents(materialItem.retourPrice),
          billablePrice: FinancialUtils.toCents(materialItem.billablePrice),
          unitPrice: FinancialUtils.toCents(materialItem.unitPrice),
          materialGroupItems: {},
          isHiddenForBudgetOverview: !materialItem.isActive,
        };
        if (budgetItemsForm && materialItem.budgetItem) {
          const budgetItem = budgetItemsForm.find(
            // @ts-expect-error
            item => materialItem.budgetItem.uuid === item.uuid,
          );
          if (budgetItem) {
            formattedMaterialGroup[isoUUID.unwrap(group.uuid)].projectMaterialGroupItems[
              isoUUID.unwrap(materialItem.uuid)
            ].budgetItem = {
              unitPrice: budgetItem.unitPrice || 0,
              discount: budgetItem.discount || 0,
            };
          }
        }

        materialItem.materialGroupItemsForServer
          .filter((item: MTMaterialGroupItem) => item.actionTeam !== null)
          .map(m => {
            index++;
            if (m.uuid || m.qty) {
              formattedMaterialGroup[
                isoUUID.unwrap(group.uuid)
              ].projectMaterialGroupItems[
                isoUUID.unwrap(materialItem.uuid)
              ].materialGroupItems[isoUUID.unwrap(m.uuid) || `new_${index}`] = {
                transported: m.transported,
                estimated: m.estimated,
                qty: m.qty,
                actionTeam: m.actionTeam,
              };
            }
          });
      });
    });
    return formattedMaterialGroup;
  }

  static filterDictionaryMaterials(
    materialGroups: ProjectMaterialGroup[],
    dictionaryMaterials: DictionaryMaterial[],
  ): DictionaryMaterial[] {
    // eslint-disable-next-line functional/no-let
    let allMaterials: ProjectMaterialGroupItem[] = [];
    materialGroups.map(group => allMaterials.push(...group.projectMaterialGroupItems));
    allMaterials = allMaterials.filter(m => m.dictionaryMaterial);
    return dictionaryMaterials.filter(
      dMaterial =>
        !allMaterials.find(
          // @ts-expect-error
          material => material.dictionaryMaterial.uuid === dMaterial.uuid,
        ),
    );
  }

  static cleanActionTeam(team: ActionTeam): ActionTeam {
    return {
      uuid: team && team.uuid,
      type: (team && team.type) || ActionTeamType.Main,
      // @ts-expect-error
      name: null,
      // @ts-expect-error
      description: null,
      // @ts-expect-error
      logo: null,
      // @ts-expect-error
      channel: null,
      // @ts-expect-error
      project: null,
      materialGroups: [],
      materialGroupItems: [],
      transports: [],
      // @ts-expect-error
      normalHours: null,
      // @ts-expect-error
      breakHours: null,
      // @ts-expect-error
      substractBreakHoursForClient: null,
      // @ts-expect-error
      substractBreakHoursForHR: null,
      // @ts-expect-error
      contactCount: null,
      services: [],
      scheduleStartLocations: [],
      scheduleAssignments: [],
      // @ts-expect-error
      compositionReservePercentage: null,
      compositionRoles: [],
      // @ts-expect-error
      billablePrice: null,
      // @ts-expect-error
      margin: null,
      // @ts-expect-error
      unitPrice: null,
    };
  }

  static prepareMaterialsGroupsForClient(
    groups: ProjectMaterialGroup[],
    actionTeams: ActionTeam[],
  ): MTMaterialGroupForm[] {
    // @ts-expect-error
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return groups.map((materialGroup: ProjectMaterialGroup) => {
      const projectMaterialGroupItems: ProjectMaterialGroupItem[] =
        materialGroup.projectMaterialGroupItems;
      return {
        ...materialGroup,
        projectMaterialGroupItems: projectMaterialGroupItems
          .map((item: ProjectMaterialGroupItem) => ({
            ...item,
            isActive: !item.isHiddenForBudgetOverview,
            // @ts-expect-error
            materialGroupItems: item.materialGroupItems.map(
              (matItem: MaterialGroupItem) => ({
                uuid: matItem.uuid,
                name: matItem.name,
                qty: matItem.qty,
                estimated: matItem.estimated,
                transported: matItem.transported,
                dictionaryMaterial: matItem.dictionaryMaterial || emptyReccuringMaterial,
                dictionarySampleUnit: matItem.dictionarySampleUnit || emptySampleMaterial,
                projectMaterialGroupItem: null,
                projectMaterialGroup: matItem.projectMaterialGroup,
                // @ts-expect-error
                actionTeam: MTCalculations.cleanActionTeam(matItem.actionTeam),
                inChecklist: matItem.inChecklist,
                iceBoxes: matItem.iceBoxes,
                iceCubes: matItem.iceCubes,
              }),
            ),
            projectMaterialGroup: null,
            materialGroupItemsForServer: actionTeams.map((actionTeam: ActionTeam) => {
              // @ts-expect-error
              const currentMaterialGroupItem: MaterialGroupItem =
                // @ts-expect-error
                item.materialGroupItems.find(
                  (materialItem: MaterialGroupItem) =>
                    materialItem.actionTeam &&
                    materialItem.actionTeam.uuid === actionTeam.uuid,
                );
              return currentMaterialGroupItem ||
                materialGroup.type === MaterialGroupTypes.Recurring ||
                materialGroup.type === MaterialGroupTypes.Clothes
                ? {
                    actionTeam: actionTeam.uuid,
                    qty:
                      (currentMaterialGroupItem && currentMaterialGroupItem.qty) || null,
                    transported:
                      (currentMaterialGroupItem &&
                        currentMaterialGroupItem.transported) ||
                      null,
                    estimated:
                      (currentMaterialGroupItem && currentMaterialGroupItem.estimated) ||
                      null,
                    uuid:
                      (currentMaterialGroupItem && currentMaterialGroupItem.uuid) || null,
                  }
                : emptyMTMaterialGroupItem;
            }),
            billablePrice: item.billablePrice / 100,
            unitPrice: item.unitPrice / 100,
            dictionaryMaterial: item.dictionaryMaterial || emptyReccuringMaterial,
            dictionarySampleUnit: item.dictionarySampleUnit || emptySampleMaterial,
          }))
          .sort(sortByCreatedAt),
      };
    });
  }
}
